import { lazy, Suspense } from 'react';
import { Trans } from '@lingui/react/macro';
import { Crosshair, Loader2, MapPinOff } from 'lucide-react';
import { Button } from '@/common/components/ui/button';
import { useLocationPicker } from '@/features/geolocation/hooks/useLocationPicker';
import type { AddressInput, LocationValue } from '@/features/geolocation/types/location';
import { LocationCoordinates } from './subcomponents/location-coordinates/LocationCoordinates';
import { LocationMapSkeleton } from './subcomponents/location-map-skeleton/LocationMapSkeleton';
import { LocationStatus } from './subcomponents/location-status/LocationStatus';

// Keeps ~150 kB of leaflet out of the main bundle. The boundary below must stay
// free of data fetching — a re-suspend would unmount and re-initialise the map.
const LocationMap = lazy(() => import('./subcomponents/location-map/LocationMap'));

export interface LocationPickerProps {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
  address: AddressInput;
  /** Nonce; increment on address-field blur to arm a debounced auto-locate. */
  locateSignal: number;
  disabled?: boolean;
}

export function LocationPicker({
  value,
  onChange,
  address,
  locateSignal,
  disabled,
}: LocationPickerProps) {
  const picker = useLocationPicker({ value, onChange, address, locateSignal });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={picker.onLocateClick}
          disabled={disabled || !picker.canLocate || picker.isLocating}
        >
          {picker.isLocating ? <Loader2 className="animate-spin" /> : <Crosshair />}
          <Trans>Locate from address</Trans>
        </Button>

        {picker.hasPosition && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={picker.onClear}
            disabled={disabled}
          >
            <MapPinOff />
            <Trans>Clear position</Trans>
          </Button>
        )}
      </div>

      <LocationStatus picker={picker} />

      <Suspense fallback={<LocationMapSkeleton />}>
        <LocationMap
          coordinates={picker.coordinates}
          bounds={picker.bounds}
          viewportNonce={picker.viewportNonce}
          onPick={picker.onMapPick}
        />
      </Suspense>

      <LocationCoordinates value={value} picker={picker} disabled={disabled} />

      <p className="text-xs text-muted-foreground">
        <Trans>Geocoding: © OpenStreetMap contributors · Altitude: Open-Elevation</Trans>
      </p>
    </div>
  );
}
